/**
 * Payload shaping for the manual "create vehicle card" dialog.
 *
 * Extracted from CreateVehicleCardManuallyDialog so the vocabulary contract with the
 * backend is testable without mounting the dialog.
 *
 * HISTORY - why this exists as its own module:
 * the dialog used to run every enum answer through a `toApiValue` helper that translated
 * the form's technical key into the SWEDISH LABEL ("gasoline" -> "Bensin",
 * "excellent" -> "Utmärkt"), on the guess that "backend may expect Swedish". The backend
 * did not: it uppercased the string and checked it against a whitelist of enum members,
 * silently falling back to GASOLINE / GOOD on a miss. The result was that every manually
 * created card stored condition = GOOD whatever the admin picked, and an "El", "Etanol"
 * or "Plug-in hybrid" pick was stored as petrol.
 *
 * The contract now: send the TECHNICAL KEYS the selects already carry. The backend's
 * shared normalizers (core/utils/fuelType.ts, core/utils/vehicleEnumSpelling.ts) accept
 * them case-insensitively and REJECT anything they do not recognise, so a vocabulary
 * drift like the above fails loudly instead of corrupting a row.
 */

import type { CreateVehicleCardManuallyRequest } from "@/types/vehicleCard";
import { colorLabels } from "@/i18n/labels/vehicleLabels";

export type ManualVehicleCardForm = {
  regNumber: string;
  vehicleType: "car" | "mc" | "";
  make: string;
  model: string;
  engine: string;
  year: string;
  mileage: string;
  fuelType: string;
  drivetype: string;
  transmission: string;
  color: string;
  type: string;
  condition: string;
  horsepower: string;
};

/**
 * The payload is exactly the endpoint's request type - no local restatement, so a wire
 * change shows up here as a compile error instead of a silent mismatch.
 */
export type ManualVehicleCardPayload = CreateVehicleCardManuallyRequest;

// ---------------------------------------------------------------------------
// Numeric field validation
//
// `parseInt("abc", 10)` is NaN and `parseInt("2024kg", 10)` is 2024 - the form's number
// inputs let both through, and neither the payload builder nor the API checked. A NaN
// reached the wire as `null` after JSON serialisation, so a typo silently created a card
// with no year or no mileage at all. These predicates are the frontend half of the
// guard; the backend rejects the same values loudly (VehicleCardValidation).
// ---------------------------------------------------------------------------

/** Oldest model year the form accepts. */
export const MIN_MODEL_YEAR = 1900;
/** Newest model year the form accepts - next year's models go on sale this year. */
export const maxModelYear = (now: Date = new Date()): number => now.getFullYear() + 1;

/** A whole number, with no trailing junk. Rejects "", "abc", "20.5", "2024kg", "1e3". */
export function parseStrictInteger(raw: string | number | null | undefined): number | undefined {
  if (raw === null || raw === undefined) return undefined;
  const text = String(raw).trim();
  if (!/^-?\d+$/.test(text)) return undefined;
  const value = Number(text);
  return Number.isSafeInteger(value) ? value : undefined;
}

export const isValidModelYear = (raw: string, now?: Date): boolean => {
  const value = parseStrictInteger(raw);
  return value !== undefined && value >= MIN_MODEL_YEAR && value <= maxModelYear(now);
};

export const isValidMileage = (raw: string): boolean => {
  const value = parseStrictInteger(raw);
  return value !== undefined && value >= 0;
};

/** Optional field: blank is valid, a non-empty value must be a non-negative integer. */
export const isValidOptionalCount = (raw: string): boolean => {
  if (!raw || raw.trim() === "") return true;
  const value = parseStrictInteger(raw);
  return value !== undefined && value >= 0;
};

/** Every numeric rule the form enforces, in one place so submit and render agree. */
export function numericFieldsValid(form: ManualVehicleCardForm, now?: Date): boolean {
  return (
    isValidModelYear(form.year, now) &&
    isValidMileage(form.mileage) &&
    isValidOptionalCount(form.horsepower)
  );
}

/**
 * Storage vocabulary for the two free-text columns.
 *
 * The card table stores what the vehicle lookup emits, which is SWEDISH ("Kombi",
 * "Grå"), and the backend lowercases it. This form's selects carry technical keys, and
 * those two vocabularies agree for BODY TYPE - the keys are already the Swedish words
 * (`kombi`, `halvkombi`, `suv`, `minibuss`), and every one of them is a key of the
 * backend's `bodyTypeToAutoUncleMap`. Body type is therefore sent as the key, unchanged.
 *
 * COLOUR is where they diverge: those keys are ENGLISH (`yellow`, `gray`), so a manually
 * created card stored "yellow" where every other writer stored "Grå"/"grå". The colour
 * is mapped through the app's own sv label map so all writers agree.
 *
 * Note the body-type keys are sent RATHER than the Swedish labels on purpose. The label
 * for `coupe` is "Coupé"; sending labels would mean relying on the backend carrying an
 * accented alias for every label in both locales. (It does carry "coupé" since
 * 2026-09-20, but only to cover rows written before carInfoMapper aliased it - not as a
 * licence to send labels.) The fixture guard in the tests pins the emitted value.
 */
const SWEDISH_COLOR_LABELS = colorLabels("sv");

/** Technical colour key -> the Swedish word the other card writers store. */
export function toStoredColor(key: string): string {
  return SWEDISH_COLOR_LABELS[key] ?? key;
}

/** Blank optional string -> undefined, so the backend applies its column default. */
const optional = (value: string | undefined): string | undefined =>
  value && value.trim() !== "" ? value : undefined;

export function buildCreateVehicleCardPayload(
  ownerId: string,
  form: ManualVehicleCardForm,
): ManualVehicleCardPayload {
  const isMc = form.vehicleType === "mc";

  return {
    ownerId,
    vehicleDetails: {
      regNumber: form.regNumber,
      make: form.make,
      model: form.model,
      engine: form.engine,
      // Strict parse: the caller must have validated first (numericFieldsValid). The
      // non-null assertions are safe there and a loud NaN here would be preferable to a
      // silent null on the wire, which is what parseInt used to produce.
      year: parseStrictInteger(form.year)!,
      mileage: parseStrictInteger(form.mileage)!,
      // Technical keys, verbatim from the select - no label translation.
      fuelType: form.fuelType,
      // `drivetype` is DRIVE type (fwd/rwd/4wd/awd), not the gearbox. The backend no
      // longer writes it into the `transmission` column (user ruling 2026-09-03) and
      // there is no drive-type column yet, so it is sent but not persisted. It is
      // deliberately NOT sent as `transmission`.
      drivetype: isMc ? undefined : optional(form.drivetype),
      // The gearbox, sent under its own name. Optional - absent leaves the column on its
      // Prisma default rather than guessing. It is deliberately NEVER derived from
      // `drivetype`, which is the mistake this whole chain existed to fix.
      transmission: optional(form.transmission),
      color: toStoredColor(form.color),
      type: isMc ? undefined : optional(form.type),
      condition: optional(form.condition),
      // Narrowed rather than run through `optional`, which widens to string: the
      // endpoint accepts only "car" | "mc". The old cast hid this.
      vehicleType: form.vehicleType === "" ? undefined : form.vehicleType,
      horsepower: parseStrictInteger(form.horsepower),
    },
  };
}
